/**
 * Security utility functions for the application
 */

// A value is never escaped more than this many times, so decoding gives up
// rather than looping forever on input shaped like an entity.
const MAX_DECODE_PASSES = 10;

const decodeOnce = (input: string): string =>
  input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/');

/**
 * Decodes HTML entities back to readable characters.
 *
 * Repeats until the value stops changing. Builds released before profile text
 * was stored raw escaped on every save, so an old row can hold several layers
 * ('&#x2F;' then '&amp;#x2F;' then '&amp;amp;#x2F;') and a single pass would
 * leave the reader looking at entity soup.
 *
 * @param input String with HTML entities to decode
 * @returns Decoded string
 */
export const decodeHtmlEntities = (input: string): string => {
  if (!input) {
    return '';
  }

  let decoded = input;

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass++) {
    const next = decodeOnce(decoded);

    if (next === decoded) {
      break;
    }

    decoded = next;
  }

  return decoded;
};

/**
 * Normalises user text on its way into the database.
 *
 * Text is stored exactly as the member typed it. Escaping belongs at the point
 * where a value is rendered into HTML, not in storage: React escapes
 * everything the apps render, and the string-built HTML in the emails and the
 * server-rendered pages escapes through web/lib/htmlEscape.js. Escaping on the
 * way in instead meant every save re-escaped the previous save's output, so a
 * deal breaker decayed from 'mamas/daddys' to 'mamas&amp;amp;#x2F;daddys'.
 *
 * Values written by an older build are decoded on the way through, so saving a
 * profile repairs the rows that build escaped.
 *
 * @param input String to normalise
 * @returns The text as typed
 */
export const normalizeUserText = (input: string): string => {
  if (!input) {
    return '';
  }

  return decodeHtmlEntities(input);
};

/**
 * Normalises every string in an object, including strings inside arrays and
 * nested objects.
 *
 * @param obj Object to normalise
 * @returns New object with normalised strings
 */
export const normalizeUserContent = <T extends Record<string, any>>(obj: T): T => {
  if (!obj) {
    return obj;
  }

  const result: Record<string, any> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (typeof value === 'string') {
        result[key] = normalizeUserText(value);
      } else if (Array.isArray(value)) {
        result[key] = value.map((item: any) =>
          typeof item === 'string' ? normalizeUserText(item) :
          typeof item === 'object' && item !== null ? normalizeUserContent(item) :
          item
        );
      } else if (typeof value === 'object' && value !== null) {
        result[key] = normalizeUserContent(value);
      } else {
        result[key] = value;
      }
    }
  }

  return result as T;
};
